/**
 * En qué punto está el timbrado de un documento electrónico de Costa Rica.
 *
 * Business Central ya lo cuenta todo en la respuesta de
 * `Importaciones_estadoFactura` --la misma que el terminal llama para la clave--:
 *
 * ```json
 * { "clave": "5061109260031011458800020000104…", "estadoElectronico": "con_referencia",
 *   "motivoElectronico": "", "mensaje": "" }
 * ```
 *
 * Hasta ahora se leía `clave` y se tiraban las otras tres en la misma línea en
 * que llegaban, así que el cajero no tenía forma de saber si su documento se
 * había timbrado o por qué no.
 *
 * ## El matiz que hace falta respetar, o el aviso miente
 *
 * **`con_referencia` no significa «timbrado».** Significa «tiene la referencia
 * que necesita para poder timbrar». Medido el 11/09/2026 en Costa Rica DEV: tres
 * notas de crédito --`VNR15-000308`, `VNR15-000310` y `VNR15-000305`-- están en
 * `con_referencia` y **no tienen clave**.
 *
 * Por eso la señal de que un documento está timbrado es **la clave**, y no el
 * estado. El estado sirve para lo otro: explicar la ausencia.
 *
 * ## El vocabulario, medido
 *
 * Sobre 61 documentos de Costa Rica --32 en DEV y 29 en producción--,
 * `estadoElectronico` sólo toma dos valores: `con_referencia` y
 * `sin_referencia`. Y `motivoElectronico` trae texto ya redactado para que lo
 * lea una persona: «El abono no dice qué factura corrige: no va a timbrar.»
 *
 * Por eso acá no se traduce ni se reescribe ese texto: se pasa tal cual. El día
 * que Business Central añada un motivo nuevo, el terminal lo enseña sin tener
 * que aprenderlo.
 */

/** En qué punto está, desde el punto de vista de quien imprime el comprobante. */
export const ESTADO_DEL_TIMBRADO = {
  /** Tiene clave: está timbrado y el comprobante puede imprimirse entero. */
  TIMBRADO: "timbrado",
  /** Todavía no, pero va camino de ello. Reimprimir más tarde lo completa. */
  EN_CAMINO: "enCamino",
  /** No va a timbrar, y Business Central dice por qué. */
  NO_TIMBRARA: "noTimbrara",
  /**
   * Hacienda lo rechazó. A diferencia de `NO_TIMBRARA`, **tiene arreglo**: se
   * corrige el dato y se vuelve a timbrar desde Business Central.
   */
  RECHAZADO: "rechazado",
  /** No se pudo preguntar. **No** es lo mismo que «sin timbrar». */
  DESCONOCIDO: "desconocido",
};

/**
 * Cómo se nombra cada estado en pantalla. Lo leen la etiqueta del listado
 * (`TimbradoTag`) y su filtro (`filtroDelEstado.js`): un solo sitio, para que el
 * filtro no pueda ofrecer «Sin timbrar» mientras la etiqueta dice otra cosa.
 */
export const NOMBRE_DEL_TIMBRADO = {
  [ESTADO_DEL_TIMBRADO.TIMBRADO]: "Timbrado",
  [ESTADO_DEL_TIMBRADO.EN_CAMINO]: "Timbre pendiente",
  [ESTADO_DEL_TIMBRADO.NO_TIMBRARA]: "Sin timbrar",
  [ESTADO_DEL_TIMBRADO.RECHAZADO]: "Rechazado por Hacienda",
};

/** Lo que tiene que hacer quien ve un rechazo. Hoy no hay otro camino. */
export const INDICACION_DE_RECHAZO = "Corrige el dato y vuelve a timbrar desde Business Central.";

/** El valor que Business Central usa cuando al documento le falta su referencia. */
const SIN_REFERENCIA = "sin_referencia";

/**
 * Lo que contesta Panamá en `estadoElectronico`, del contrato acordado con la
 * sesión de ExceliaOne el 17/09/2026 (acuerdo B4).
 *
 * Se mapea explícito y no por las reglas de abajo, porque **sin esto dos de las
 * cinco palabras significarían lo contrario de lo que dicen**: cualquier valor que
 * no sea `sin_referencia` y venga sin motivo cae en `EN_CAMINO`, así que un
 * `desconocido` --«no se pudo preguntar»-- se leería como «va camino de timbrar».
 * Optimismo sobre una lectura que falló.
 *
 * No choca con Costa Rica, que sólo manda `con_referencia` y `sin_referencia`.
 *
 * `sin_registro` --«miré el log y no había fila»-- se lee como `EN_CAMINO` y no
 * como `NO_TIMBRARA`: puede que el envío todavía no haya ocurrido, y afirmar lo
 * definitivo sobre algo que aún puede llegar es el error caro. Esperar de más
 * cuesta unos segundos; decir «no timbró» de un documento emitido puede hacer que
 * alguien lo anule.
 */
const ESTADO_DE_PANAMA = {
  timbrado: ESTADO_DEL_TIMBRADO.TIMBRADO,
  rechazado: ESTADO_DEL_TIMBRADO.RECHAZADO,
  en_proceso: ESTADO_DEL_TIMBRADO.EN_CAMINO,
  sin_registro: ESTADO_DEL_TIMBRADO.EN_CAMINO,
  // El único «no va a timbrar» que Business Central puede afirmar: la empresa
  // tiene apagada la factura electrónica, así que no hay circuito. No vale al
  // revés --encendida no promete que un documento del TPV se envíe, porque por la
  // API no se envía (B1)--, y por eso todo lo demás sigue siendo `sin_registro`.
  no_emite: ESTADO_DEL_TIMBRADO.NO_TIMBRARA,
  desconocido: ESTADO_DEL_TIMBRADO.DESCONOCIDO,
};

const texto = (valor) => String(valor ?? "").trim();

/**
 * Si el código que devolvió el emisor es de rechazo.
 *
 * `codigoElectronico` es texto: `""` cuando no hay respuesta del emisor y `"0"`
 * cuando la aceptó. Medido en Costa Rica DEV el 12/09/2026: `"0"` en las
 * facturas timbradas y `"03"` en `VFR15-009021` y `VFR15-009023`, que Hacienda
 * rechazó por un teléfono con dos números. Business Central no interpreta la
 * tabla de códigos del emisor, así que acá tampoco: cualquier otro valor es
 * rechazo.
 *
 * Sólo decide **sin clave**. Un documento con clave está timbrado, diga lo que
 * diga el código. Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H6.
 */
export const esCodigoDeRechazo = (codigo) => {
  const valor = texto(codigo);
  return valor !== "" && valor !== "0";
};

/**
 * Lee la respuesta de `Importaciones_estadoFactura` y dice en qué punto está.
 *
 * @param {object|null} respuesta Lo que devolvió `consultarEstadoFactura`.
 *   `null` significa «no se pudo preguntar», que no es «no está timbrado».
 * @returns {{estado: string, clave: string, referencia: string, motivo: string}}
 */
export const leerElTimbrado = (respuesta) => {
  // Sin respuesta no se afirma nada. Dar por «sin timbrar» un documento del que
  // no se pudo preguntar pondría un aviso falso sobre un comprobante bueno.
  if (!respuesta || typeof respuesta !== "object") {
    return {
      estado: ESTADO_DEL_TIMBRADO.DESCONOCIDO,
      clave: "",
      referencia: "",
      motivo: "",
    };
  }

  const clave = texto(respuesta.clave);
  const referencia = texto(respuesta.estadoElectronico);
  // Sólo `motivoElectronico`, y el respaldo por `mensaje` se retiró a propósito.
  //
  // `mensaje` es donde Business Central deja su diagnóstico de **peticiones mal
  // formadas**, no el motivo de Hacienda. Y `traerElTimbrado` llama a
  // `estadoFactura` sin `tienda` ni `caja` (`claveDeHacienda.js`), así que el día
  // que BC decidiera diagnosticar ese caso, un texto interno suyo se imprimiría
  // en el tiquete fiscal del cliente y pintaría el documento en rojo «Sin
  // timbrar».
  //
  // Es el compromiso del TPV en la ronda 10 de
  // `contrato-tipo-documento-electronico.md`, y a cambio BC se compromete a no
  // reutilizar `mensaje` para el motivo de Hacienda. El **texto** del emisor
  // tiene clave propia --`mensajeElectronico`-- y vive en `respuestaDelEmisor.js`,
  // por un camino que no toca esta máquina de estados. Lo que sí entra, desde
  // H6, es su **código**, y sólo cuando no hay clave.
  const motivo = texto(respuesta.motivoElectronico);

  // La clave manda sobre el estado: es el único dato que prueba el timbrado.
  if (clave) {
    return { estado: ESTADO_DEL_TIMBRADO.TIMBRADO, clave, referencia, motivo };
  }

  // Panamá dice el estado con todas las letras, así que se le hace caso antes que
  // a las reglas de abajo, que están escritas para lo que manda Costa Rica.
  if (ESTADO_DE_PANAMA[referencia]) {
    return { estado: ESTADO_DE_PANAMA[referencia], clave: "", referencia, motivo };
  }

  // Un rechazo del emisor tiene arreglo, y el aviso tiene que decirlo. Antes caía
  // en «no se va a timbrar», que para un rechazo de estructura es falso: se
  // corrige el teléfono y timbra.
  if (esCodigoDeRechazo(respuesta.codigoElectronico)) {
    return { estado: ESTADO_DEL_TIMBRADO.RECHAZADO, clave: "", referencia, motivo };
  }

  // Sin clave, hay dos formas de no estar timbrado, y al cajero le importan
  // cosas distintas en cada una: una se arregla esperando y la otra no.
  const noVaATimbrar = referencia === SIN_REFERENCIA || Boolean(motivo);

  return {
    estado: noVaATimbrar ? ESTADO_DEL_TIMBRADO.NO_TIMBRARA : ESTADO_DEL_TIMBRADO.EN_CAMINO,
    clave: "",
    referencia,
    motivo,
  };
};

/**
 * Lo que se le dice al cajero cuando el comprobante no lleva clave.
 *
 * Se usa en el hueco del ticket donde iría la clave numérica. El motivo de
 * Business Central se enseña tal cual cuando existe: está escrito para leerse.
 *
 * Un rechazo se nombra como tal y nada más: el ticket lo lleva el cliente, y la
 * indicación de qué corregir es para quien atiende. Esa va en el listado
 * (`INDICACION_DE_RECHAZO`).
 *
 * @param {{estado: string, motivo: string}} timbrado
 * @returns {string} El aviso, o `""` si no hay nada que decir.
 */
export const avisoDelTimbrado = ({ estado, motivo } = {}) => {
  if (estado === ESTADO_DEL_TIMBRADO.TIMBRADO) return "";
  if (estado === ESTADO_DEL_TIMBRADO.RECHAZADO) return "Rechazado por Hacienda.";
  if (motivo) return motivo;
  if (estado === ESTADO_DEL_TIMBRADO.NO_TIMBRARA) return "Este documento no se va a timbrar.";
  if (estado === ESTADO_DEL_TIMBRADO.EN_CAMINO) return "Pendiente de timbrado.";
  return "No se pudo consultar el timbrado.";
};

/**
 * Lee de la fila local en qué punto quedó el timbrado.
 *
 * Vivía en `TimbradoTag.js`, que es un componente con antd; se trajo aquí para
 * que el tablero de Inicio la use sin arrastrar la librería de pantalla. El
 * componente la reexporta.
 *
 * La clave es la prueba: `con_referencia` no significa timbrado, sólo que el
 * documento tiene lo que necesita para poder timbrar --medido el 11/09/2026, con
 * tres notas de crédito en ese estado y sin clave--.
 *
 * Sin clave, un código de rechazo del emisor manda sobre lo demás: es la única
 * de las formas de no estar timbrado que se arregla corrigiendo el dato. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H6.
 *
 * @param {object} factura Fila local, con `Clave`, `CodigoElectronico`,
 *   `Electronic_Document_Status` y `Mensaje`.
 * @returns {{estado: string, motivo: string}|null} `null` si nadie preguntó todavía.
 */
export const timbradoDeLaFila = (factura) => {
  if (texto(factura?.Clave)) {
    return { estado: ESTADO_DEL_TIMBRADO.TIMBRADO, motivo: "" };
  }

  const motivo = texto(factura?.Mensaje);

  if (esCodigoDeRechazo(factura?.CodigoElectronico)) {
    return { estado: ESTADO_DEL_TIMBRADO.RECHAZADO, motivo };
  }

  const referencia = texto(factura?.Electronic_Document_Status);

  // Sin nada anotado no se afirma que falte el timbre: puede que todavía no se
  // haya preguntado. Decir «sin timbrar» por no saberlo sería un aviso falso.
  if (!motivo && !referencia) return null;

  return {
    estado:
      motivo || referencia === SIN_REFERENCIA
        ? ESTADO_DEL_TIMBRADO.NO_TIMBRARA
        : ESTADO_DEL_TIMBRADO.EN_CAMINO,
    motivo,
  };
};
